import { useState } from 'react';
import { NavBar } from '../../components/NavBar.tsx';
import { Avatar } from '../../components/Avatar.tsx';
import { Icon } from '../../components/Icon.tsx';
import { Alert, Sheet as BottomSheet } from '../../components/Overlay.tsx';
import { StatusPill, PriorityPill } from '../../components/Pill.tsx';
import { ErrorState } from '../../components/DataState.tsx';
import { ApiError } from '../../api/client.ts';
import { addItem, deleteItem, deleteSheet, patchItem } from '../../api/rooms.ts';
import { itemLabel } from '../../domain.ts';
import type { Entry, Item, ItemKind, Person, PrayerNote, Sheet } from '../../domain.ts';
import { cardMeta } from '../book/shared.ts';
import { OptionMenu } from './OptionMenu.tsx';

/**
 * F4 모임 기록지 — 그리고 그 위에 뜨는 것들.
 *
 *   F4-0     항목 3종을 다 더한 뒤의 모습 (같은 화면의 다른 상태)
 *   F4-1     그룹원이 볼 때 — 연필이 자기 줄에만, ⋯ 와 ＋ 가 없음
 *   F4-3     줄의 내용을 눌러 그 자리에서 펼쳐 보기
 *   F4-4     ⋯ 기록지 메뉴 (수정 · 삭제)
 *   F4-5     ＋ 항목 추가 (말씀 나눔 · 결단)
 *
 * 항목이 셋이고 뜻이 다릅니다 — **말씀 나눔**은 설교 말씀을 두고 말씀 · 해석 ·
 * 질문지, **기도 노트**는 말씀을 듣고 생각난 기도 제목, **결단**은 말씀을 듣고
 * 결단한 것. 기도 노트와 결단은 사람마다 한 줄씩 가지고, 말씀 나눔은
 * 기록지에 한 장입니다.
 *
 * **누가 무엇을 할 수 있는지는 서버가 정해 보냅니다** (canManage · canEdit).
 * 화면이 ownerId 를 비교해 판단하면 같은 규칙이 두 곳에 생깁니다.
 */

type Over =
  | { at: 'none' }
  | { at: 'menu' }
  | { at: 'add' }
  | { at: 'delete' }
  | { at: 'verse'; item: Item };

interface Props {
  sheet: Sheet;
  /** 내 식별자 — 「나」 꼬리표와 내 줄 판별에 씁니다 */
  meId: string | null;
  ownerId: string;
  onBack: () => void;
  /** 줄을 눌렀습니다 — 부모가 F4-2 시트를 엽니다 */
  onWrite: (entry: Entry, kind: ItemKind) => void;
  /** 구성원의 사진 · 이름을 눌렀습니다 — D2 · D2-1 */
  onPerson: (person: Person) => void;
  /** 이어진 노트 한 장을 눌렀습니다 — 읽기 전용으로 엽니다 */
  onOpenNote: (note: PrayerNote) => void;
  onEdit: () => void;
  onDeleted: () => void;
  onChanged: (sheet: Sheet) => void;
}

export function SheetView({
  sheet,
  meId,
  ownerId,
  onBack,
  onWrite,
  onPerson,
  onOpenNote,
  onEdit,
  onDeleted,
  onChanged,
}: Props) {
  const [over, setOver] = useState<Over>({ at: 'none' });
  /** 펼쳐 놓은 줄 — F4-3 */
  const [opened, setOpened] = useState<Set<string>>(new Set());
  /** 접어 둔 말씀 나눔 */
  const [folded, setFolded] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const toggleOpen = (entryId: string) =>
    setOpened((was) => {
      const next = new Set(was);
      if (next.has(entryId)) next.delete(entryId);
      else next.add(entryId);
      return next;
    });

  const apply = async (run: () => Promise<Sheet>) => {
    setBusy(true);
    setFailure(null);
    try {
      onChanged(await run());
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : '바꾸지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  const hasDecision = sheet.items.some((i) => i.kind === 'DECISION');

  return (
    <div className="screen">
      <NavBar
        title={sheet.title}
        subtitle={sheet.metOn.replace(/-/g, '.')}
        start={{ icon: 'back', label: '기록지 목록', onClick: onBack }}
        /* ⋯ 와 ＋ 는 그룹장에게만 — 그룹원 화면(F4-1)에는 없습니다 */
        end={
          sheet.canManage
            ? {
                icon: 'more',
                label: '기록지 메뉴',
                pressed: over.at === 'menu',
                onClick: () => setOver({ at: 'menu' }),
              }
            : undefined
        }
      />

      <div className="screen__body">
        {failure && <ErrorState message={failure} onRetry={() => setFailure(null)} />}

        {sheet.items.map((item) => (
          <article className="card" key={item.itemId}>
            <div className="item__head">
              <span className="pill pill--status" data-status={pillTone(item.kind)}>
                <Icon name={itemIcon(item.kind)} size={11} />
                {itemLabel[item.kind]}
              </span>

              <span className="item__tools">
                {/* 말씀 나눔은 접을 수 있습니다 — 기도 제목만 보고 싶을 때 */}
                {item.kind === 'VERSE_SHARING' && (
                  <button
                    type="button"
                    className="item__tool"
                    onClick={() =>
                      setFolded((was) => {
                        const next = new Set(was);
                        if (next.has(item.itemId)) next.delete(item.itemId);
                        else next.add(item.itemId);
                        return next;
                      })
                    }
                  >
                    {folded.has(item.itemId) ? '펼치기 ▾' : '접기 ▴'}
                  </button>
                )}

                {sheet.canManage && (
                  <ItemTools
                    item={item}
                    count={sheet.items.length}
                    busy={busy}
                    onMove={(to) => void apply(() => patchItem(item.itemId, { sortOrder: to }))}
                    onEdit={() => setOver({ at: 'verse', item })}
                    onDelete={() => void apply(() => deleteItem(item.itemId))}
                  />
                )}
              </span>
            </div>

            {item.kind === 'VERSE_SHARING' ? (
              <VerseBody item={item} folded={folded.has(item.itemId)} />
            ) : (
              <div className="stack stack--tight">
                {item.entries.map((entry) => (
                  <EntryRow
                    key={entry.entryId}
                    entry={entry}
                    kind={item.kind}
                    meId={meId}
                    ownerId={ownerId}
                    open={opened.has(entry.entryId)}
                    onToggle={() => toggleOpen(entry.entryId)}
                    onWrite={() => onWrite(entry, item.kind)}
                    onPerson={onPerson}
                    onOpenNote={onOpenNote}
                  />
                ))}
              </div>
            )}
          </article>
        ))}

        <p className="hint">
          {sheet.canManage
            ? '＋ 로 말씀 나눔과 결단을 더할 수 있습니다. 이모티콘 · 코멘트는 여기 없습니다 — 기도 노트에서만 씁니다.'
            : '내 줄의 연필로 기도 제목을 적습니다. 다른 사람의 이름을 누르면 그 사람의 대표 기도 노트를 볼 수 있습니다.'}
        </p>
      </div>

      {sheet.canManage && (
        <button
          type="button"
          className="fab"
          aria-label="항목 추가"
          onClick={() => setOver({ at: 'add' })}
        >
          <Icon name="plus" size={23} />
        </button>
      )}

      {over.at === 'menu' && (
        <OptionMenu
          onClose={() => setOver({ at: 'none' })}
          options={[
            { icon: 'pencil', label: '기록지 수정', onClick: onEdit },
            {
              icon: 'trash',
              label: '기록지 삭제',
              crit: true,
              onClick: () => setOver({ at: 'delete' }),
            },
          ]}
        />
      )}

      {/* F4-5 — 기도 노트는 처음부터 있으므로 여기 없습니다 */}
      {over.at === 'add' && (
        <BottomSheet title="무엇을 더할까요?" onClose={() => setOver({ at: 'none' })}>
          <button
            type="button"
            className="mrow"
            onClick={() => {
              setOver({ at: 'none' });
              void apply(() => addItem(sheet.sheetId, 'VERSE_SHARING'));
            }}
          >
            <Icon name="verse" size={20} />
            <span className="mrow__grow">
              말씀 나눔
              <span className="mrow__sub">설교 말씀 · 해석 · 질문지</span>
            </span>
          </button>

          <button
            type="button"
            className="mrow"
            disabled={hasDecision}
            onClick={() => {
              setOver({ at: 'none' });
              void apply(() => addItem(sheet.sheetId, 'DECISION'));
            }}
          >
            <Icon name="check" size={20} />
            <span className="mrow__grow">
              결단
              <span className="mrow__sub">
                {hasDecision
                  ? '이미 있습니다'
                  : '참여한 구성원마다 한 칸씩 생깁니다'}
              </span>
            </span>
          </button>

          <p className="hint">녹음은 아직 준비 중입니다.</p>
        </BottomSheet>
      )}

      {/* F4-4-2 — 무엇이 지워지고 무엇이 남는지 나눠 적습니다 */}
      {over.at === 'delete' && (
        <Alert
          title="이 기록지를 지울까요?"
          onClose={() => setOver({ at: 'none' })}
          actions={
            <>
              <button
                type="button"
                className="btn btn--secondary"
                onClick={() => setOver({ at: 'none' })}
              >
                취소
              </button>
              <button
                type="button"
                className="btn btn--danger"
                onClick={async () => {
                  try {
                    await deleteSheet(sheet.sheetId);
                    onDeleted();
                  } catch (e) {
                    setOver({ at: 'none' });
                    setFailure(e instanceof ApiError ? e.message : '지우지 못했습니다.');
                  }
                }}
              >
                삭제
              </button>
            </>
          }
        >
          <p>이 기록지의 기도 노트 · 말씀 나눔 · 결단이 모두 지워집니다.</p>
          <p className="alert__note">
            Prayer Book 에 저장한 기도 노트와 공유한 대표 기도제목의 <b>원본은 그대로 남습니다.</b>
          </p>
        </Alert>
      )}

      {over.at === 'verse' && (
        <VerseWriter
          item={over.item}
          onClose={() => setOver({ at: 'none' })}
          onSaved={(next) => {
            onChanged(next);
            setOver({ at: 'none' });
          }}
        />
      )}
    </div>
  );
}

/* ── 줄 ───────────────────────────────────────────────────────── */

/**
 * 한 사람 한 줄 — F4 · F4-1 · F4-3.
 *
 * 머리에 사진 · 이름과 연필이 오고, 아래에 제목과 내용 첫 줄이 옵니다.
 * **사진 · 이름을 누르면 친구 팝업**(D2 · D2-1), **내용을 누르면 그 자리에서
 * 펼쳐집니다**(F4-3) — 화면을 옮기지 않아 다른 사람 줄이 위아래에 남습니다.
 *
 * 연필은 canEdit 이 true 인 줄에만 보입니다. 그룹장 화면에서는 모든 줄,
 * 그룹원 화면에서는 자기 줄뿐입니다.
 */
function EntryRow({
  entry,
  kind,
  meId,
  ownerId,
  open,
  onToggle,
  onWrite,
  onPerson,
  onOpenNote,
}: {
  entry: Entry;
  kind: ItemKind;
  meId: string | null;
  ownerId: string;
  open: boolean;
  onToggle: () => void;
  onWrite: () => void;
  onPerson: (person: Person) => void;
  onOpenNote: (note: PrayerNote) => void;
}) {
  const name = entry.member.name ?? '이름 없음';
  const mine = entry.member.userId === meId;
  const shared = entry.mode === 'SHARED' && entry.notes.length > 0;
  /** WRITTEN 이면서 Prayer Book 에 담긴 줄 — 글자는 원본에서 옵니다 */
  const linked = entry.mode === 'WRITTEN' && entry.notes.length > 0;
  const title = linked ? entry.notes[0].title : entry.title;
  const body = linked ? entry.notes[0].body : entry.body;

  return (
    <div className="rec">
      <div className="rec__head">
        <button type="button" className="rec__who" onClick={() => onPerson(entry.member)}>
          <Avatar name={name} seed={entry.member.userId} />
          <span className="rec__name">
            {name}
            {mine && <span className="rec__tag">나</span>}
            {!mine && entry.member.userId === ownerId && <span className="rec__tag">그룹장</span>}
          </span>
        </button>

        {entry.canEdit && (
          <button
            type="button"
            className="rec__edit"
            aria-label={`${name}의 ${kind === 'DECISION' ? '결단' : '기도 제목'} 적기`}
            onClick={onWrite}
          >
            <Icon name="pencil" size={14} />
          </button>
        )}
      </div>

      {/* 내용 부분을 누르면 펼쳐집니다. 아직 빈 줄은 펼칠 것이 없습니다 */}
      <div
        className="rec__body"
        {...(entry.written
          ? {
              role: 'button',
              tabIndex: 0,
              onClick: onToggle,
              onKeyDown: (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onToggle();
                }
              },
            }
          : {})}
      >
        {!entry.written && <span className="rec__empty">아직 작성 전</span>}

        {/* 대표 공유 줄 — 고른 제목이 점과 함께 한 줄씩 */}
        {shared &&
          !open &&
          entry.notes.map((n) => (
            <span className="rec__shared" key={n.id}>
              <span className="rec__dot" />
              {n.title}
            </span>
          ))}

        {/* 펼친 대표 공유 줄 — 한 장씩 카드로. 공유받은 노트(F1-1)와 같은 모양 */}
        {shared && open && (
          <>
            {entry.notes.map((n) => (
              <button
                key={n.id}
                type="button"
                className="xcard"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenNote(n);
                }}
              >
                <span className="xcard__pills">
                  <StatusPill status={n.status} />
                  {n.priority !== 'NORMAL' && <PriorityPill priority={n.priority} />}
                  <span className="card__meta">{cardMeta(n)}</span>
                </span>
                <span className="card__title">{n.title}</span>
                {n.body !== '' && <span className="rec__full">{n.body}</span>}
              </button>
            ))}
            <button type="button" className="rec__fold">
              접기 ▴
            </button>
          </>
        )}

        {/* 직접 적은 줄 */}
        {!shared && entry.written && (
          <>
            {title !== '' ? (
              <>
                <span className="rec__title">{title}</span>
                {body !== '' &&
                  (open ? (
                    <span className="rec__full">{body}</span>
                  ) : (
                    <span className="rec__line">{body}</span>
                  ))}
              </>
            ) : (
              /* 제목이 없으면 내용이 제목 자리에 옵니다 */
              <span className={open ? 'rec__full' : 'rec__line rec__line--lead'}>{body}</span>
            )}

            {/* 대신 적어 준 줄임을 줄에서도 알립니다 */}
            {entry.writtenBy && !open && (
              <span className="rec__line">그룹장 {entry.writtenBy.name ?? ''}이 적음</span>
            )}

            {open && (
              <button type="button" className="rec__fold">
                접기 ▴
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ── 말씀 나눔 ─────────────────────────────────────────────────── */

function VerseBody({ item, folded }: { item: Item; folded: boolean }) {
  const empty =
    item.verse === '' && item.interpretation === '' && item.questions === '';

  if (empty) return <span className="rec__empty">아직 작성 전</span>;

  return (
    <>
      {item.verse !== '' && <span className="card__title">{item.verse}</span>}
      {!folded && (
        <>
          {item.interpretation !== '' && (
            <>
              <span className="verse__label">해석</span>
              <span className="verse__text">{item.interpretation}</span>
            </>
          )}
          {item.questions !== '' && (
            <>
              <span className="verse__label">질문지</span>
              <span className="verse__text">{item.questions}</span>
            </>
          )}
        </>
      )}
    </>
  );
}

/** 말씀 · 해석 · 질문지를 적는 시트 */
function VerseWriter({
  item,
  onClose,
  onSaved,
}: {
  item: Item;
  onClose: () => void;
  onSaved: (sheet: Sheet) => void;
}) {
  const [verse, setVerse] = useState(item.verse);
  const [interpretation, setInterpretation] = useState(item.interpretation);
  const [questions, setQuestions] = useState(item.questions);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const save = async () => {
    setBusy(true);
    setFailure(null);
    try {
      onSaved(await patchItem(item.itemId, { verse, interpretation, questions }));
    } catch (e) {
      setFailure(e instanceof ApiError ? e.message : '저장하지 못했습니다.');
      setBusy(false);
    }
  };

  return (
    <BottomSheet title="말씀 나눔" onClose={onClose}>
      <div className="sheet__scroll">
        {failure && <ErrorState message={failure} onRetry={() => setFailure(null)} />}

        <label className="field">
          <span className="field__label">말씀</span>
          <input
            className="input"
            value={verse}
            autoFocus
            placeholder="요한복음 15:1–8"
            onChange={(e) => setVerse(e.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">해석</span>
          <textarea
            className="input input--tall"
            value={interpretation}
            onChange={(e) => setInterpretation(e.target.value)}
          />
        </label>

        <label className="field">
          <span className="field__label">질문지</span>
          <textarea
            className="input input--tall"
            value={questions}
            onChange={(e) => setQuestions(e.target.value)}
          />
        </label>
      </div>

      <button type="button" className="btn btn--primary" disabled={busy} onClick={save}>
        {busy ? '저장 중…' : '저장'}
      </button>
    </BottomSheet>
  );
}

/* ── 항목의 우측 도구 ─────────────────────────────────────────── */

/**
 * 순서 바꾸기와 지우기 — F4-0 의 우측 ⋯.
 *
 * 내비바의 ⋯(F3-1 · F4-4)와 달리 **바텀시트로 올립니다.** 그 메뉴는 화면
 * 맨 위 버튼에 붙어 펼쳐지지만, 항목의 ⋯ 는 화면 중간에 있어 같은 방식으로
 * 두면 누른 곳과 열린 곳이 어긋납니다.
 *
 * 끌어 옮기는 대신 «위로 · 아래로» 버튼을 씁니다. 항목이 둘 셋뿐이고, 끌기는
 * 목록이 함께 스크롤되는 화면에서 자주 어긋납니다 — 어긋나면 사용자는
 * 자기가 뭘 잘못했는지 모릅니다.
 */
function ItemTools({
  item,
  count,
  busy,
  onMove,
  onEdit,
  onDelete,
}: {
  item: Item;
  count: number;
  busy: boolean;
  onMove: (to: number) => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [open, setOpen] = useState(false);
  const label = itemLabel[item.kind];

  return (
    <>
      <button
        type="button"
        className="item__tool"
        aria-label={`${label} 메뉴`}
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <Icon name="more" size={13} />
      </button>

      {open && (
        <BottomSheet title={label} onClose={() => setOpen(false)}>
          {item.kind === 'VERSE_SHARING' && (
            <button
              type="button"
              className="mrow"
              onClick={() => {
                setOpen(false);
                onEdit();
              }}
            >
              <Icon name="pencil" size={20} />
              <span className="mrow__grow">
                말씀 나눔 적기
                <span className="mrow__sub">말씀 · 해석 · 질문지</span>
              </span>
            </button>
          )}

          <button
            type="button"
            className="mrow"
            disabled={busy || item.sortOrder === 0}
            onClick={() => {
              setOpen(false);
              onMove(item.sortOrder - 1);
            }}
          >
            <Icon name="back" size={20} />
            <span className="mrow__grow">위로 옮기기</span>
          </button>

          <button
            type="button"
            className="mrow"
            disabled={busy || item.sortOrder >= count - 1}
            onClick={() => {
              setOpen(false);
              onMove(item.sortOrder + 1);
            }}
          >
            <Icon name="forward" size={20} />
            <span className="mrow__grow">아래로 옮기기</span>
          </button>

          {/* 기도 노트는 기록지의 뼈대라 지울 수 없습니다 */}
          {item.kind !== 'PRAYER_NOTE' && (
            <button
              type="button"
              className="mrow mrow--danger"
              onClick={() => {
                setOpen(false);
                onDelete();
              }}
            >
              <Icon name="trash" size={20} />
              <span className="mrow__grow">
                {label} 지우기
                <span className="mrow__sub">적힌 내용이 함께 사라집니다</span>
              </span>
            </button>
          )}

          <button type="button" className="btn btn--secondary" onClick={() => setOpen(false)}>
            취소
          </button>
        </BottomSheet>
      )}
    </>
  );
}

/** 항목마다 색과 아이콘이 다릅니다 — 스크롤하며 훑을 때 아이콘이 먼저 읽힙니다 */
function itemIcon(kind: ItemKind) {
  if (kind === 'VERSE_SHARING') return 'verse' as const;
  if (kind === 'DECISION') return 'check' as const;
  return 'doc' as const;
}

function pillTone(kind: ItemKind) {
  if (kind === 'VERSE_SHARING') return 'PRAYING';
  if (kind === 'DECISION') return 'ANSWERED';
  return 'IN_PROGRESS';
}
